import { useEffect, useRef, useState } from "react";
import styles from "./fontSelector.module.css";
import { ChevronDown, Search, AlertTriangle } from "lucide-react";
import { Font } from "../../../../../../../../../sdkStructs";

interface FontSelectorProps {
  value: Font;
  onValueChange: (value: Font) => void;
  label: string;
  fonts: any[];
  loadFont: (font: any) => Promise<void>;
}

export function FontSelector({ value, label, onValueChange, fonts, loadFont }: Readonly<FontSelectorProps>) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [hasMissingVariants, setHasMissingVariants] = useState(false);
  const [missingBold, setMissingBold] = useState(false);
  const [missingItalic, setMissingItalic] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isMissingVariant = (variant: string | undefined | null): boolean => {
    return variant === undefined || variant === null || variant === "" || variant === " ";
  };

  useEffect(() => {
    if (value) {
      const boldMissing = isMissingVariant(value.Bold);
      const italicMissing = isMissingVariant(value.Italic);
      
      setMissingBold(boldMissing);
      setMissingItalic(italicMissing);
      setHasMissingVariants(boldMissing || italicMissing);
    } else {
      setHasMissingVariants(false);
    }
  }, [value]);

  const filteredFonts = fonts.filter((font) =>
    font.Name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleFontSelect = async (font: any) => {
    await loadFont(font);
    onValueChange({
      Name: font.Name,
      Regular: font.Regular,
      Bold: font.Bold,
      Italic: font.Italic,
      BoldItalic: font.BoldItalic,
    });
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className={styles.fontSelectorContainer}>
      <label className={styles.label}>{label}</label>
      <div className={styles.fontSelector} ref={dropdownRef}>
        <button className={styles.selectorButton} onClick={() => setIsOpen(!isOpen)} style={{ fontFamily: value.Name }}>
          {value.Name}
          <ChevronDown className={styles.chevron} />
        </button>

        {isOpen && (
          <div className={styles.dropdown}>
            <div className={styles.searchContainer}>
              <Search className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search for a font family"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
            </div>

            <div className={styles.fontList}>
              {filteredFonts.map((font) => (
                <div key={font.Name} className={styles.fontOptionContainer}>
                  <div
                    onClick={() => handleFontSelect(font)}
                    className={styles.fontOption}
                    style={{ fontFamily: font.Name }}
                  >
                    {font.Name}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {hasMissingVariants && (
          <div className={styles.variantWarning}>
            <AlertTriangle size={14} className={styles.warningIcon} />
            <span>
              Missing: {missingBold ? 'Bold' : ''}
              {missingBold && missingItalic ? ' and ' : ''}
              {missingItalic ? 'Italic' : ''}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
